import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Container from '../layout/Container';
import Button from '../ui/Button';

// Google Form URL and ID - Used for form submission
// Form ID: 1FAIpQLScw-LYOFnhY8w8ATEGPpxBfISF8dOdmRn1PBsMbUDF-374zsg

// Form field entry IDs - These map to specific questions in the Google Form
// These IDs must match the entry IDs in the Google Form HTML source
const GOOGLE_FORM_NAME_FIELD = "entry.2005620554";     // Name field
const GOOGLE_FORM_EMAIL_FIELD = "entry.1045781291";    // Email field
const GOOGLE_FORM_SUBJECT_FIELD = "entry.572878628";   // Subject field (from HTML source)
const GOOGLE_FORM_MESSAGE_FIELD = "entry.839337160";   // Message field

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    try {
      // Create a URL string manually using Google's prefill format (usp=pp_url is important)
      let baseUrl = `https://docs.google.com/forms/d/e/1FAIpQLScw-LYOFnhY8w8ATEGPpxBfISF8dOdmRn1PBsMbUDF-374zsg/viewform?usp=pp_url`;
      
      // Add each form parameter to the URL with proper encoding
      baseUrl += `&${GOOGLE_FORM_NAME_FIELD}=${encodeURIComponent(formData.name)}`;
      baseUrl += `&${GOOGLE_FORM_EMAIL_FIELD}=${encodeURIComponent(formData.email)}`;
      
      // Add subject and message fields to the URL with proper encoding
      baseUrl += `&${GOOGLE_FORM_SUBJECT_FIELD}=${encodeURIComponent(formData.subject)}`;
      baseUrl += `&${GOOGLE_FORM_MESSAGE_FIELD}=${encodeURIComponent(formData.message)}`;
      
      // Add a debug parameter to help identify this version of the form submission
      baseUrl += `&debugVersion=${encodeURIComponent("v1.3-final")}`;
      
      // For debugging - log the URL to console
      console.log("Form URL with parameters:", baseUrl);
      
      // Open the form URL in a new tab
      window.open(baseUrl, '_blank');
      
      // Show success message
      setFormStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset form status after 5 seconds
      setTimeout(() => {
        setFormStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
    }
  };

  return (
    <ContactSection id="contact">
      <Container>
        <SectionTitle>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            Get In Touch
          </motion.div>
        </SectionTitle>

        <ContactDescription>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            I'm always open to new opportunities, collaborations, or just a friendly chat. 
            Feel free to reach out to me if you have any questions or want to connect!
          </motion.div>
        </ContactDescription>

        <ContactGrid>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ContactInfo>
              <ContactInfoItem>
                <ContactIcon>📧</ContactIcon>
                <ContactMethod>
                  <ContactMethodTitle>Email</ContactMethodTitle>
                  <ContactMethodValue>
                    <a href="mailto:mschristopheranto@gmail.com">mschristopheranto@gmail.com</a>
                  </ContactMethodValue>
                </ContactMethod>
              </ContactInfoItem>

              <ContactInfoItem>
                <ContactIcon>💼</ContactIcon>
                <ContactMethod>
                  <ContactMethodTitle>LinkedIn</ContactMethodTitle>
                  <ContactMethodValue>
                    <a href="https://www.linkedin.com/in/christopher-anto/" target="_blank" rel="noopener noreferrer">
                      linkedin.com/in/christopher-anto
                    </a>
                  </ContactMethodValue>
                </ContactMethod>
              </ContactInfoItem>

              <ContactInfoItem>
                <ContactIcon>💻</ContactIcon>
                <ContactMethod>
                  <ContactMethodTitle>GitHub</ContactMethodTitle>
                  <ContactMethodValue>
                    <a href="https://github.com/IntergrityTechnologies" target="_blank" rel="noopener noreferrer">
                      github.com/IntergrityTechnologies
                    </a>
                  </ContactMethodValue>
                </ContactMethod>
              </ContactInfoItem>
            </ContactInfo>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 30 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ContactForm onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel>Name</FormLabel>
                <FormInput
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Email</FormLabel>
                <FormInput
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Subject</FormLabel>
                <FormInput
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Message</FormLabel>
                <FormTextarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  required
                />
              </FormGroup>

              <FormButton>
                <button type="submit" style={{ display: 'none' }}></button>
              <Button fullWidth size="lg" onClick={() => document.forms[0].requestSubmit()} disabled={formStatus === 'submitting'}>
                  {formStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </Button>
              </FormButton>

              {formStatus === 'success' && (
                <FormMessage success>
                  Thank you! A new tab has opened with your pre-filled form. Please click the "Submit" button in the new tab to complete your submission.
                </FormMessage>
              )}

              {formStatus === 'error' && (
                <FormMessage>
                  Sorry, there was an error opening the form. Please try again or contact me directly at mschristopheranto@gmail.com.
                </FormMessage>
              )}
            </ContactForm>
          </motion.div>
        </ContactGrid>
      </Container>
    </ContactSection>
  );
};

// Styling components
const ContactSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xl} 0;
  padding-top: ${({ theme }) => theme.spacing.lg}; /* Significantly reduced padding at top to bring section closer to navbar */
  background-color: ${({ theme }) => theme.colors.background};
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  background: linear-gradient(to right, #FFFFFF, ${({ theme }) => theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const ContactDescription = styled.p`
  text-align: center;
  max-width: 600px;
  margin: 0 auto ${({ theme }) => theme.spacing.xxl};
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.125rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.boxShadow.normal};
  transition: transform ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ContactIcon = styled.div`
  font-size: 1.75rem;
  line-height: 1;
`;

const ContactMethod = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContactMethodTitle = styled.h4`
  font-size: 1.125rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ContactMethodValue = styled.p`
  font-size: 0.95rem;
  margin: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-word;
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    transition: color ${({ theme }) => theme.transitions.fast};
    
    &:hover {
      color: ${({ theme }) => theme.colors.secondary};
    }
  }
`;

const ContactForm = styled.form`
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.boxShadow.normal};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-weight: 500;
  font-size: 0.95rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  transition: border-color ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  resize: vertical;
  transition: border-color ${({ theme }) => theme.transitions.fast};
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }
`;

const FormButton = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xl};
`;

const FormMessage = styled.div<{ success?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ success }) => 
    success ? 'rgba(48, 209, 88, 0.1)' : 'rgba(255, 55, 95, 0.1)'};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  color: ${({ theme, success }) => 
    success ? theme.colors.secondary : theme.colors.accent};
  font-size: 0.875rem;
  text-align: center;
`;

export default Contact;
